<template>
  <!-- 4. 高频告警聚类（含结论与归因） -->
  <section class="report-section">
    <h2 class="section-title">
      <!-- <v-icon size="20" color="var(--color-primary)">mdi-chart-bar</v-icon> -->
      高频告警聚类
    </h2>
    <div v-if="clusterSummary" class="cluster-summary">
      <v-icon size="18" color="var(--color-primary)">mdi-lightbulb-on-outline</v-icon>
      {{ clusterSummary }}
    </div>
    <div class="cluster-chart">
      <div v-for="item in pagedClusterList" :key="item.alarmCode" class="cluster-block">
        <div class="cluster-row" @click="$emit('open-code', item)">
          <div class="cluster-label-group">
            <div class="cluster-main-label">
              <v-icon size="14" :class="getCategoryClass(item.category)" class="cluster-cat-icon">{{ getCategoryIcon(item.category) }}</v-icon>
              <span class="alarm-code" :style="{ color: getCategoryColor(item.category) }">{{ item.alarmCode }}</span>
              <span class="alarm-desc">{{ item.content }}</span>
            </div>
          </div>
          <div class="cluster-visual">
            <div class="visual-bar-area">
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: getClusterBarWidth(item.count) }">
                  <span class="bar-label">{{ item.count }}次</span>
                </div>
              </div>
            </div>
            <div class="visual-stats">
              <span class="stat-badge neutral">{{ item.stationCount }}站</span>
              <span class="stat-badge warn">{{ item.unrecoveredCount }}未恢复</span>
            </div>
          </div>
        </div>
        <div class="cluster-insight">
          <v-icon size="14" color="#8792a6">mdi-information-outline</v-icon>
          <span>{{ item.insight }}</span>
          <!-- <a class="insight-toggle" @click="toggleCluster(item.alarmCode)">
            {{ expandedClusters.includes(item.alarmCode) ? '收起排查' : '排查建议' }}
          </a> -->
        </div>
        <ul v-if="expandedClusters.includes(item.alarmCode)" class="diagnosis-steps">
          <li v-if="!item.diagnosisSteps?.length" class="diagnosis-empty">暂无排查建议</li>
          <li v-for="(step, idx) in item.diagnosisSteps" :key="idx">{{ step }}</li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getCategoryClass, getCategoryIcon, getCategoryInfo } from './category'

const CATEGORY_COLORS = {
  SAFETY: 'rgba(255, 30, 30, 1)',
  INCOME: 'rgba(245, 158, 11, 1)',
  NORMAL: 'rgba(58, 104, 255, 1)',
}
function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.NORMAL
}

const props = defineProps({
  clusterSummary: { type: String, default: '' },
  clusterList: { type: Array, default: () => [] },
})
defineEmits(['open-code'])

// 排查建议展开状态：纯 UI 状态，只影响本组件自身展示，无需父组件感知
const expandedClusters = ref([])
function toggleCluster(alarmCode) {
  const idx = expandedClusters.value.indexOf(alarmCode)
  if (idx >= 0) expandedClusters.value.splice(idx, 1)
  else expandedClusters.value.push(alarmCode)
}

// 只展示 TOP10（接口已按触发次数倒序）
const pagedClusterList = computed(() => props.clusterList.slice(0, 10))

// 高频告警聚类柱状图宽度计算
const maxClusterCount = computed(() => Math.max(...props.clusterList.map(item => item.count), 1))
function getClusterBarWidth(count) {
  return `${(count / maxClusterCount.value) * 100}%`
}
</script>

<style scoped lang="scss">
@import './sectionShell.scss';
@import './category.scss';

/* 高频告警聚类 - 横向柱状图风格 */
.cluster-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: rgba(58, 104, 255, 0.04);
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #2e2e2e;
  line-height: 1.7;
}

.cluster-chart {
  display: flex;
  flex-direction: column;
}

.cluster-block {
  padding: 10px 8px 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}

.cluster-row {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 16px;
  align-items: center;
  cursor: pointer;
}

.cluster-label-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.cluster-main-label {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.cluster-cat-icon {
  flex-shrink: 0;

  &.cat-safety {
    color: rgba(255, 30, 30, 1);
  }

  &.cat-income {
    color: rgba(245, 158, 11, 1);
  }

  &.cat-normal {
    color: rgba(58, 104, 255, 1);
  }
}

.alarm-code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.alarm-desc {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #2e2e2e;
  font-weight: 500;
}

.alarm-level-badge {
  height: 36px;
  width: 36px;
  flex-shrink: 0;
}

.cluster-visual {
  display: flex;
  align-items: center;
  gap: 10px;
}

.visual-bar-area {
  flex: 1;
  min-width: 0;
}

.bar-track {
  height: 16px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3A68FF 0%, #6B8CFF 100%);
  display: flex;
  align-items: center;
  padding: 0 8px;
  transition: width 0.4s ease;
  min-width: 52px;
  border-radius: 999px;
}

.bar-label {
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.visual-stats {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  // height: 20px;
  padding: 0 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 999px;
  font-size: 11px;
  color: rgba(46, 46, 46, 0.7);
  font-weight: 500;
  white-space: nowrap;

  &.neutral {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(46, 46, 46, 0.7);
  }

  &.warn {
    background: rgba(220, 38, 38, 0.1);
    color: #DC2626;
    font-weight: 500;
  }

  &.trend-up {
    background: rgba(239, 68, 68, 0);
    color: #EF4444;
    font-size: 12px;
    font-weight: 600;
  }

  &.trend-down {
    background: rgba(16, 185, 129, 0);
    color: #10B981;
    font-size: 12px;
    font-weight: 600;
  }
}

/* 聚类归因结论 */
.cluster-insight {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  // padding-left: 2px;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.65);
  line-height: 1.5;

  .insight-toggle {
    flex-shrink: 0;
    color: #3A68FF;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

.diagnosis-steps {
  margin: 10px 0 0;
  padding-left: 24px;
  color: rgba(46, 46, 46, 0.75);
  font-size: 12px;
  line-height: 1.9;

  li {
    margin: 6px 0;
  }

  .diagnosis-empty {
    color: rgba(46, 46, 46, 0.45);
    list-style: none;
    margin-left: -24px;
  }
}

@media (max-width: 900px) {
  .cluster-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.cluster-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 16px;
  color: rgba(46, 46, 46, 0.6);
  font-size: 13px;
}
</style>
